import { useState } from 'react';
import type {
  DHWPressureData,
  ColdMainType,
} from '../types';
import { validateDHWPressureData, VALIDATION_RANGES, parseFiniteNumber } from '../validation';
import { useModalGuard } from '../hooks/useModalGuard';
import { DiscardDialog } from '../components/DiscardDialog';

const COLD_MAIN_TYPE_OPTIONS: { value: ColdMainType; label: string }[] = [
  { value: 'mdpe_25_blue', label: '25mm blue MDPE' },
  { value: 'mdpe_20', label: '20mm MDPE' },
  { value: 'copper_22', label: '22mm copper' },
  { value: 'copper_15', label: '15mm copper' },
  { value: 'lead', label: 'Lead' },
  { value: 'unknown', label: 'Not assessed yet' },
];

interface Props {
  data: DHWPressureData;
  onSave: (data: DHWPressureData) => void;
  onClose: () => void;
}

export function DHWPressureScreen({ data, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<DHWPressureData>({ ...data });
  const [rawInputs, setRawInputs] = useState<Record<string, string>>({
    mainsFlowLpm: data.mainsFlowLpm !== null ? String(data.mainsFlowLpm) : '',
    mainsStaticPressureBar: data.mainsStaticPressureBar !== null ? String(data.mainsStaticPressureBar) : '',
    mainsDynamicFlowLpm: data.mainsDynamicFlowLpm !== null ? String(data.mainsDynamicFlowLpm) : '',
    mainsDynamicPressureBar: data.mainsDynamicPressureBar !== null ? String(data.mainsDynamicPressureBar) : '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Use modal guard hook for consistent discard behavior
  const {
    hasUnsavedChanges,
    showDiscardDialog,
    markDirty,
    markClean,
    handleClose,
    confirmDiscard,
    cancelDiscard,
  } = useModalGuard({ onClose });

  const setNumericField = (key: keyof DHWPressureData, rawValue: string) => {
    // Store raw input for display (allows typing "2." before "5")
    setRawInputs(prev => ({ ...prev, [key]: rawValue }));
    
    // Convert to number | null at the boundary
    const numericValue = parseFiniteNumber(rawValue);
    setDraft(prev => ({ ...prev, [key]: numericValue }));
    markDirty();
    
    // Clear validation error for this field when user types
    if (validationErrors[key as string]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const set = <K extends keyof DHWPressureData>(key: K, value: DHWPressureData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    markDirty();
  };

  const handleSave = () => {
    // Validate all numeric inputs
    const validation = validateDHWPressureData(draft);
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    markClean();
    onSave(draft);
  };

  return (
    <>
      <div className="hub-screen-overlay">
        <div className="hub-screen" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Cold Mains Performance</h2>
            <button className="hub-screen__close" onClick={handleClose}>×</button>
          </div>

        <div className="hub-screen__body">
          <div className="hub-screen__section">
            <h3 className="hub-screen__section-title">Cold mains inputs</h3>
            <p className="hub-screen__help">
              Capture measured cold mains capacity to assess combi and unvented viability.
            </p>

            <div className="hub-screen__field">
              <label className="hub-screen__label">Cold main type</label>
              <select
                className="hub-screen__select" 
                value={draft.coldMainType}
                onChange={e => set('coldMainType', e.target.value as ColdMainType)}
              >
                {COLD_MAIN_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="hub-screen__field">
              <label className="hub-screen__label">
                Flow (L/min)
                <span className="hub-screen__label-hint"> — {VALIDATION_RANGES.mainsFlowLpm.examples}</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                className={`hub-screen__input ${validationErrors.mainsFlowLpm ? 'hub-screen__input--error' : ''}`}
                value={rawInputs.mainsFlowLpm}
                onChange={e => setNumericField('mainsFlowLpm', e.target.value)}
                placeholder="e.g., 20"
              />
              {validationErrors.mainsFlowLpm && (
                <div className="hub-screen__error">{validationErrors.mainsFlowLpm}</div>
              )}
            </div>

            <div className="hub-screen__field">
              <label className="hub-screen__label">
                Static pressure (bar)
                <span className="hub-screen__label-hint"> — {VALIDATION_RANGES.mainsStaticPressureBar.examples}</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                className={`hub-screen__input ${validationErrors.mainsStaticPressureBar ? 'hub-screen__input--error' : ''}`}
                value={rawInputs.mainsStaticPressureBar}
                onChange={e => setNumericField('mainsStaticPressureBar', e.target.value)}
                placeholder="e.g., 2.5"
              />
              {validationErrors.mainsStaticPressureBar && (
                <div className="hub-screen__error">{validationErrors.mainsStaticPressureBar}</div>
              )}
            </div>
          </div>

          <div className="hub-screen__section">
            <h3 className="hub-screen__section-title">Dynamic pair (optional)</h3>

            <div className="hub-screen__field">
              <label className="hub-screen__label">
                Dynamic flow (L/min)
                <span className="hub-screen__label-optional"> (optional)</span>
                <span className="hub-screen__label-hint"> — {VALIDATION_RANGES.mainsDynamicFlowLpm.examples}</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                className={`hub-screen__input ${validationErrors.mainsDynamicFlowLpm ? 'hub-screen__input--error' : ''}`}
                value={rawInputs.mainsDynamicFlowLpm}
                onChange={e => setNumericField('mainsDynamicFlowLpm', e.target.value)}
                placeholder="e.g., 16"
              />
              {validationErrors.mainsDynamicFlowLpm && (
                <div className="hub-screen__error">{validationErrors.mainsDynamicFlowLpm}</div>
              )}
            </div>

            <div className="hub-screen__field">
              <label className="hub-screen__label">
                Dynamic pressure (bar)
                <span className="hub-screen__label-optional"> (optional)</span>
                <span className="hub-screen__label-hint"> — {VALIDATION_RANGES.mainsDynamicPressureBar.examples}</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                className={`hub-screen__input ${validationErrors.mainsDynamicPressureBar ? 'hub-screen__input--error' : ''}`}
                value={rawInputs.mainsDynamicPressureBar}
                onChange={e => setNumericField('mainsDynamicPressureBar', e.target.value)}
                placeholder="e.g., 1.8"
              />
              {validationErrors.mainsDynamicPressureBar && (
                <div className="hub-screen__error">{validationErrors.mainsDynamicPressureBar}</div>
              )}
            </div>
          </div>

          {(draft.mainsFlowLpm === null || draft.mainsStaticPressureBar === null) && (
            <div className="hub-screen__warning">
              ⚠️ Cold mains not assessed. Measure flow and static pressure for reliable combi/unvented recommendations.
            </div>
          )}
        </div>

        <div className="hub-screen__footer">
          <button className="hub-screen__button hub-screen__button--secondary" onClick={handleClose}>
            Cancel
          </button>
          <button className="hub-screen__button hub-screen__button--primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>

    {/* Discard changes dialog */}
    <DiscardDialog
      isOpen={showDiscardDialog}
      onConfirm={confirmDiscard}
      onCancel={cancelDiscard}
    />
  </>
);
}
